import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';
import { debounce } from 'ts-debounce';
import { safeWarn } from '@/utils/safeLog';
export const USE_MUTATION_OBSERVABLE_DEFAULT_OPTIONS = {
    config: {
        attributes: true,
        childList: true,
        subtree: true
    },
    debounceTime: 0
};
/**
 * This custom hooks abstracts the usage of the Mutation Observer with React components.
 * Watch for changes being made to the DOM tree and trigger a custom callback.
 * @param {Element} targetEl DOM element to be observed
 * @param {Function} cb callback that will run when there's a change in targetEl or any
 * child element (depending on the provided options)
 * @param {Object} options
 * @param {Object} options.config check \[options\](https://developer.mozilla.org/en-US/docs/Web/API/MutationObserver/observe)
 * @param {number} [options.debounceTime=0] a number that represents the amount of time in ms
 * that you which to debounce the call to the provided callback function
 */
const useMutationObservable = (targetEl, cb, t0) => {
    const $ = _c(9);
    const options = t0 === undefined ? USE_MUTATION_OBSERVABLE_DEFAULT_OPTIONS : t0;
    const [observer, setObserver] = useState(null);
    let t1;
    let t2;
    if ($[0] !== cb || $[1] !== options) {
        t1 = () => {
            if (!cb || typeof cb !== "function") {
                safeWarn(`You must provide a valid callback function, instead you've provided ${cb}`);
                return;
            }
            const { debounceTime } = options;
            const obs = new MutationObserver(debounceTime > 0 ? debounce(cb, debounceTime) : cb);
            setObserver(obs);
        };
        t2 = [cb, options, setObserver];
        $[0] = cb;
        $[1] = options;
        $[2] = t1;
        $[3] = t2;
    }
    else {
        t1 = $[2];
        t2 = $[3];
    }
    useEffect(t1, t2);
    let t3;
    let t4;
    if ($[4] !== observer || $[5] !== options || $[6] !== targetEl) {
        t3 = () => {
            if (!observer) {
                return;
            }
            if (!targetEl) {
                safeWarn(`You must provide a valid DOM element to observe, instead you've provided ${targetEl}`);
            }
            const { config } = options;
            ;
            try {
                observer.observe(targetEl, config);
            }
            catch (t5) {
                const e = t5;
                safeWarn("error encountered in observer.observe", e);
            }
            return () => {
                if (observer) {
                    observer.disconnect();
                }
            };
        };
        t4 = [observer, targetEl, options];
        $[4] = observer;
        $[5] = options;
        $[6] = targetEl;
        $[7] = t3;
        $[8] = t4;
    }
    else {
        t3 = $[7];
        t4 = $[8];
    }
    useEffect(t3, t4);
};
export default useMutationObservable;
